/**
 * Terrain painters shared by play and the editor (docs/PERFORMANCE.md, Phase 1).
 *
 * Each function draws through a `PaintSink`: a `Phaser.GameObjects.Graphics` (command buffer
 * replayed every frame) or a `MeshPaintSink` (tessellated once into a retained `StaticMesh`).
 * One painter, two sinks — the mesh path cannot drift from the Graphics path.
 *
 * Everything in a column fill is world-space and zoom-independent: contact bands, creases,
 * and the cap lip are world pixels, so a body's mesh only changes when the map, the sun
 * (wall lambert), or the look (cap flock) changes. Rims and hint dashes take a `strokeScale`
 * and are rebuilt once the zoom leaves the 8% band (`noteZoom`).
 */

import { mixColor } from '../colorMix.ts';
import type { Vec2 } from '../types.ts';
import {
  CAP_BLOB_CONTACT_ALPHA,
  CAP_BLOB_CONTACT_MIX,
  CAP_BLOB_RISE_PX,
  CAP_CROWN_MIX,
  CAP_CROWN_SCALE,
  CAP_LIP_ALPHA,
  CAP_LIP_MIX,
  CAP_LIP_PX,
  DEFAULT_CAP_FLOCK_AMOUNT,
  capFlockBlobs,
  scaleTowardCentroid,
  type CapKind,
} from './capFlock.ts';
import { dashedPolyline } from './checkpointLook.ts';
import {
  CONTACT_WIDTH_PX,
  CREASE_STROKE_PX,
  creaseShade,
  extrudeOffset,
  topLambert,
  wallLambert,
  wallQuads,
} from './extrude.ts';
import type { PaintSink } from './paintSink.ts';
import { tessellateRing } from './ring.ts';
import type { Sun } from './sun.ts';
import { skirtWidthPx } from './surfaces.ts';
import type { Ring } from './types.ts';

/** Temporary hidden-north-shore mark. Not the finished look — see docs/LIGHTING.md. */
export const WATERLINE_HINT_COLOR = 0xffffff;
export const WATERLINE_HINT_STROKE_PX = 1.5;
export const WATERLINE_HINT_DASH_PX = 7;
export const WATERLINE_HINT_GAP_PX = 5;

export type ColumnFillSpec = {
  ring: Ring;
  elevation: number;
  sun: Sun;
  fill: number;
  alpha: number;
  /** Cliff swatch; falls back to `fill` when 0 / undefined. */
  wallSwatch?: number;
  /** Support plateau: stacked terraces draw only the riser. */
  baseElevation: number;
  id: string;
  kind: CapKind;
  capFlock: boolean;
  capFlockAmount?: number;
};

/** Cliff colour for a wall quad: lee toward dark, lit toward warm. */
export function wallFill(swatch: number, lambert: number): number {
  const lee = mixColor(swatch, 0x1a1210, 0.55 * (1 - lambert));
  return mixColor(lee, 0xf0c878, 0.22 * lambert);
}

function offsetPoints(points: readonly Vec2[], offset: Vec2): Vec2[] {
  if (offset.x === 0 && offset.y === 0) {
    return points.slice();
  }

  return points.map((point) => ({ x: point.x + offset.x, y: point.y + offset.y }));
}

/** Flat fill of a ring (zero elevation, shallow zone, editor flat mode). */
export function fillClosedRing(sink: PaintSink, ring: Ring, fill: number, alpha: number): void {
  const points = tessellateRing(ring);

  if (points.length < 3) {
    return;
  }

  sink.fillStyle(fill, alpha);
  sink.fillPoints(points, true, true);
}

/** Zoom-scaled rim, with an optional wide translucent soft edge under it (reefs). */
export function strokeClosedRing(
  sink: PaintSink,
  ring: Ring,
  stroke: number,
  strokeWidth: number,
  alpha: number,
  softEdgeWidth = 0,
): void {
  const points = tessellateRing(ring);

  if (points.length < 3) {
    return;
  }

  if (softEdgeWidth > 0) {
    sink.lineStyle(softEdgeWidth, stroke, 0.28 * alpha);
    sink.strokePoints(points, true, true);
  }

  sink.lineStyle(strokeWidth, stroke, alpha);
  sink.strokePoints(points, true, true);
}

/** Screen-pixel rim on the plateau (or the waterline when the body is flat). */
export function strokeExtrudedRing(
  sink: PaintSink,
  ring: Ring,
  elevation: number,
  stroke: number,
  strokeWidth: number,
  alpha = 1,
): void {
  const offset = extrudeOffset(elevation);
  const points = tessellateRing(ring);

  if (points.length < 3) {
    return;
  }

  if (offset.x === 0 && offset.y === 0) {
    strokeClosedRing(sink, ring, stroke, strokeWidth, alpha);
    return;
  }

  sink.lineStyle(strokeWidth, stroke, alpha);
  sink.strokePoints(offsetPoints(points, offset), true, true);
}

/** Waterline sand band. Drawn on its own layer so the hull can sail over the outer lip. */
export function paintIslandSkirt(
  sink: PaintSink,
  ring: Ring,
  skirtM: number,
  color: number,
  alpha = 1,
): void {
  const width = skirtWidthPx(skirtM);
  const points = tessellateRing(ring);

  if (width <= 0 || points.length < 3) {
    return;
  }

  sink.lineStyle(width, color, alpha);
  sink.strokePoints(points, true, true);
}

/** Hidden-shore dashes for one body's cached runs. */
export function strokeHintRuns(
  sink: PaintSink,
  runs: readonly (readonly Vec2[])[],
  strokeScale: number,
  columnAlpha: number,
  hintAlpha: number,
): void {
  const strokeAlpha = hintAlpha * columnAlpha;

  if (strokeAlpha <= 0 || runs.length === 0) {
    return;
  }

  const dash = WATERLINE_HINT_DASH_PX * strokeScale;
  const gap = WATERLINE_HINT_GAP_PX * strokeScale;
  let any = false;

  for (const run of runs) {
    const dashes = dashedPolyline(run, false, dash, gap);

    if (dashes.length === 0) {
      continue;
    }

    if (!any) {
      sink.lineStyle(WATERLINE_HINT_STROKE_PX * strokeScale, WATERLINE_HINT_COLOR, strokeAlpha);
      any = true;
    }

    for (const segment of dashes) {
      sink.lineBetween(segment.a.x, segment.a.y, segment.b.x, segment.b.y);
    }
  }
}

/**
 * Column look: cliff quads plus a screen-up top face. Zero elevation stays a flat fill.
 * `baseElevation` is the support plateau — stacked terraces only draw the riser, not a
 * full-height pillar from the waterline. Optional cap flock after the solid top is a
 * provisional experiment (`docs/CAP-FLOCK.md`); it is not part of the column contract.
 */
export function fillExtrudedRing(sink: PaintSink, spec: ColumnFillSpec): void {
  const { ring, elevation, sun, fill, alpha, baseElevation, id, kind, capFlock } = spec;
  const capFlockAmount = spec.capFlockAmount ?? DEFAULT_CAP_FLOCK_AMOUNT;
  const offset = extrudeOffset(elevation);
  const baseOffset = extrudeOffset(Math.min(baseElevation, elevation));
  const points = tessellateRing(ring);

  if (points.length < 3) {
    return;
  }

  if (offset.x === 0 && offset.y === 0) {
    fillClosedRing(sink, ring, fill, alpha);
    return;
  }

  const wallColor = spec.wallSwatch && spec.wallSwatch > 0 ? spec.wallSwatch : fill;
  const waterCrease = mixColor(wallColor, 0x1a1210, creaseShade('water'));
  const lipCrease = mixColor(fill, 0x1a1210, creaseShade('lip'));
  const shelfCrease = mixColor(fill, 0x1a1210, creaseShade('shelf'));

  // Contact before walls: a body shorter in Y than its rise puts the back of the
  // footprint inside the south wall band. Stroking the ring after the quads paints
  // that base through the cliff face (the selected peak's own shelf / waterline).
  if (baseElevation <= 0) {
    sink.lineStyle(CONTACT_WIDTH_PX, mixColor(fill, 0x1a1210, creaseShade('water')), 0.55 * alpha);
    sink.strokePoints(points, true, true);
  } else {
    sink.lineStyle(CONTACT_WIDTH_PX, shelfCrease, 0.7 * alpha);
    sink.strokePoints(offsetPoints(points, baseOffset), true, true);
  }

  for (const quad of wallQuads(points, offset, baseOffset)) {
    sink.fillStyle(wallFill(wallColor, wallLambert(quad.outward, sun)), alpha);
    sink.fillTriangle(
      quad.waterA.x,
      quad.waterA.y,
      quad.waterB.x,
      quad.waterB.y,
      quad.topB.x,
      quad.topB.y,
    );
    sink.fillTriangle(
      quad.waterA.x,
      quad.waterA.y,
      quad.topB.x,
      quad.topB.y,
      quad.topA.x,
      quad.topA.y,
    );
    sink.lineStyle(CREASE_STROKE_PX, waterCrease, alpha);
    sink.lineBetween(quad.waterA.x, quad.waterA.y, quad.waterB.x, quad.waterB.y);
    sink.lineStyle(CREASE_STROKE_PX, lipCrease, alpha);
    sink.lineBetween(quad.topA.x, quad.topA.y, quad.topB.x, quad.topB.y);
  }

  const top = offsetPoints(points, offset);
  sink.fillStyle(mixColor(fill, 0xfff1c8, 0.12 * topLambert(sun)), alpha);
  sink.fillPoints(top, true, true);

  if (!capFlock) {
    return;
  }

  const crown = scaleTowardCentroid(top, CAP_CROWN_SCALE);

  if (crown) {
    sink.fillStyle(mixColor(fill, 0xfff1c8, CAP_CROWN_MIX), alpha);
    sink.fillPoints(crown, true, true);
  }

  const blobTint = kind === 'rock' ? 0x3d3428 : 0x1a2a14;

  for (const blob of capFlockBlobs(top, id, kind, capFlockAmount)) {
    sink.fillStyle(mixColor(fill, 0x1a1210, CAP_BLOB_CONTACT_MIX), CAP_BLOB_CONTACT_ALPHA * alpha);
    sink.fillEllipse(blob.x, blob.y, blob.rx * 2.15, blob.ry * 2.15);
    sink.fillStyle(mixColor(fill, blobTint, blob.mix), alpha);
    sink.fillEllipse(blob.x, blob.y - CAP_BLOB_RISE_PX, blob.rx * 2, blob.ry * 2);
  }

  sink.lineStyle(CAP_LIP_PX, mixColor(fill, 0x1a1210, CAP_LIP_MIX), CAP_LIP_ALPHA * alpha);
  sink.strokePoints(top, true, true);
}
